
<script setup>
  import { useEditor, EditorContent } from '@tiptap/vue-3'
  import StarterKit from '@tiptap/starter-kit'
  import Collaboration from '@tiptap/extension-collaboration'
  import CollaborationCursor from '@tiptap/extension-collaboration-cursor'
  import { inject, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
  import * as Y from 'yjs';
  import { C_WebsocketProvider } from './y-websocket.js'
  import Placeholder from '@tiptap/extension-placeholder'
  import Toolbar from './Toolbar.vue'
  import { LocationMarkerExt } from './markerPlugin'
  import { Extension } from '@tiptap/core'
  import AiPopup from './AiPopup.vue'
  import { StreamingNode } from './streamingNode'
  import FloatingMenu from '@tiptap/extension-floating-menu'
  import { BridgeEvents } from './eventBridge'
  import { constructHash, newThread } from '../actions/documentActions'
  import { waitFor } from '../utils/waitFor'
  import Icon from '../Icon.vue'
  import { Events } from '../events'
  import TaskList from '@tiptap/extension-task-list';
  import TaskItem from '@tiptap/extension-task-item';
  import Document from '@tiptap/extension-document'
  import Paragraph from '@tiptap/extension-paragraph'
  import Text from '@tiptap/extension-text'
  import Heading from '@tiptap/extension-heading'
  import { ySyncPluginKey } from 'y-prosemirror'
import { createPopper } from '@popperjs/core'
import Gutter from './Gutter.vue'
import { WS_BASE_URL } from '../env';
import { getRandomAwarenessColor } from '../awareness/awarenessUtils'
import Image from '@tiptap/extension-image'
import { getDownloadURL, getStorage, uploadBytes } from 'firebase/storage'
import { Firebase_App } from '../firebase'
import { uploadImagesAndInsertAsContent } from './imageUtils'
import DiffViewerOverlay from '../diff/DiffViewerOverlay.vue'
import DraftOverlay from './DraftOverlay.vue'
import { guessTitle } from '../utils/textUtls.js'
import { HighlightRangesExt } from './highlightPlugin.js'
import { diff_match_patch, DIFF_INSERT, DIFF_DELETE, DIFF_EQUAL } from 'diff-match-patch'
import { capturePosthog } from '../posthog.js'
import Button from '../components/Button.vue'

import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Link from '@tiptap/extension-link'

  const {documentId} = defineProps({
    documentId: String
  })

  const activeDocument = inject('document')
  const {saveDocument, replaceNavigationSilently} = inject('appActions')
  const userInfo = inject('userInfo')
  const rejectionDocumentContent = inject('rejectionDocumentContent')
  const draftText = inject('draftText')
  const draftGenerated = inject('draftGenerated')

  watch(draftGenerated, async () => {
    if (draftGenerated.value) {
      let html = draftText.value
      html = html
      .replace(/(<\/\w+>)\s*(<\w+>)/g, '$1$2')
      .replace(/^```[\w-]*\n([\s\S]*?)\n```$/gm, '$1') // backticks
      
      editor.value.commands.setContent(html);

      const div = document.createElement('div')
      div.innerHTML = draftText.value      
      const title = guessTitle(div)
      if (title) {
        activeDocument.value.title = title
      }
      saveDocument().then(() => {
        replaceNavigationSilently(constructHash({docId: activeDocument.value.id}))
      })
    }
  })

  const ydoc = new Y.Doc()
  // const provider = new WebrtcProvider(
  //   '67a194f3-89e4-800d-93b0-bff66c3ec3dc', 
  //   ydoc, 
  //   {       
  //     //run it first!: PORT=5555 node ./node_modules/y-webrtc/bin/server.js
  //     signaling: ['ws://localhost:5555/'] 
      
  //   }
  // );

  //HOST=localhost PORT=5577  node ./node_modules/y-websocket/bin/server.cjs

  const provider = new C_WebsocketProvider(WS_BASE_URL, 'collab?room=' + documentId, ydoc, {
    disableBc: true
  })
  const providerDestroyer = () => provider.destroy(); // closes the socket properly

  // const basePath = ["DocumentContent", documentId];
  // const provider = new FirestoreProvider(Firebase_App, ydoc, basePath);


  provider.on('sync', (isSynced) => {
    if (isSynced) {      
      console.log('Document has been fully loaded and synced.');
    }
  });

  let docSaveToken = null;
  let firstTimeTouch = true;

  ydoc.on('updateV2', (update, origin, doc, transaction) => {
    if (transaction?.local && activeDocument.value.owner === userInfo.uid) {
      activeDocument.value.modified = Date.now()
      if (docSaveToken !== null) {
        return;
      }
      docSaveToken = setTimeout(() => {
        docSaveToken = null
        saveDocument()
      }, firstTimeTouch ? 0 : 1000 * 30);
          
      firstTimeTouch = false
    }
  })
  // const yXmlFragment = ydoc.getXmlFragment('prosemirror')

  const state = reactive({
    aiPopup: null
  })

  const CharizardExtension = Extension.create({
    addKeyboardShortcuts() {
      return {
        'Mod-k': () => this.editor.commands.aiHelp(),
      }
    },

    addCommands() {
      return {
        
        closeAiPopup: () => {
          state.aiPopup = null
        },

        openAiPopup: (text) => () => {
          state.aiPopup = Date.now()
        },

        aiHelp: color => ({ editor, chain}) => {
          chain().markToFix().run()
          newThread(activeDocument.value)
          setTimeout(() => {
            document
            .querySelector('.main-chat .prompt textarea')
            ?.focus()
          })          
        } 
      }
    },
    
  })

  const editor = useEditor({
    content: "",
    extensions: [
      StarterKit.configure({
        dropcursor: {
          width: 3,
          color: '#4f46e5'
        },
        heading: {
          levels: [1, 2, 3, 4]
        },
        history: false
      }),
      Image.configure({
        // allowBase64: true,
      }),
      StreamingNode,
      CharizardExtension,
      LocationMarkerExt,
      HighlightRangesExt,

      Table.configure({
        resizable: true
      }),
      TableRow,
      TableHeader,
      TableCell,
      Link.configure({
        openOnClick: true, // links open when clicked
        autolink: true,    // automatically converts URLs to links
        linkOnPaste: true, // automatically detects URLs on paste
      }),
      Collaboration.configure({
        document: ydoc,
      }),
      CollaborationCursor.configure({
        provider: provider, // Y.js provider
        user: {
          id: userInfo.id,
          name: userInfo.name,
          color: getRandomAwarenessColor(),
        },
      }),

      
      TaskList,
      TaskItem.configure({
        nested: true,
        HTMLAttributes: {
          draggable: 'true',
        },
      }),

      // Placeholder.configure({
      //   placeholder: 'Write something … It’ll be shared with everyone else looking at this example.',
      // }),

    ],

    
    onSelectionUpdate: ({ editor, transaction }) => {
      Events.dispatch(Events.SELECTION, { editor, transaction })
    }
    
  })

  let gutter = null;
  const editorNode = ref(null);
  
  function scrollDispatcher() {
    Events.dispatch(Events.SCROLL, {editor: editor.value, editorNode: editorNode.value})
  }

  function awarenessListener() {
    Events.dispatch(Events.AWARENESS, provider.awareness.getStates()) 
  }

  let disposeListeners;
  let deferedUpdateToken = null;
  onMounted(() => {
    window.addEventListener("beforeunload", providerDestroyer);
    window.__EDITOR = editor.value

    disposeListeners = BridgeEvents(editor)    

    editorNode.value.addEventListener('scroll', scrollDispatcher);
    Events.dispatch(Events.SELECTION, {editor: editor.value} )

    provider.awareness.on('change', awarenessListener)
    awarenessListener()

    editor.value.on('transaction', ({ transaction }) => {
      if (transaction.getMeta('y-sync$')) {
        return
      }

      if (transaction.docChanged) {

        if (rejectionDocumentContent.value !== null) {
          rejectionDocumentContent.value = null
          setTimeout(() => editor.value.chain().dropHighlights().run());
          Events.dispatch(Events.PRIME_CONTEXT, [
            {
              kind: 'user',
              text: 'I accepted your draft.'
            },
            {
              kind: 'ai',
              text: 'OK.'
            }
          ]);
          capturePosthog('ai_draft_accepted')
        }
      }
    })
  })

  onBeforeUnmount(() => {
    window.__EDITOR = null
    provider.awareness.off('change', awarenessListener)
    window.removeEventListener("beforeunload", providerDestroyer);
    providerDestroyer() 

    if (deferedUpdateToken !== null) {
      clearTimeout(deferedUpdateToken)
      deferedUpdateToken = null
    }

    disposeListeners();    
    listenerGroup.dispose();
    editorNode.value.removeEventListener('scroll', scrollDispatcher);
    Events.dispatch(Events.MARK_FOR_FIX, null)
  })

  function makeSureFocus(e) {
    !editor.value.isFocused && editor.value.commands.focus()
  }

  async function handleDrop(event) {
    const storage = getStorage(Firebase_App)

    const files = event.dataTransfer.files
    if (!files.length) return

    const images = []
    for (const file of files) {
      if (file.type.startsWith('image/')) {
        images.push(file)
      }
    }

    await uploadImagesAndInsertAsContent(editor.value, documentId, images)
  }


  function rejectNewVersionDraft() {
    if (rejectionDocumentContent.value === null) {
      return;
    }
    editor.value.commands.setContent(rejectionDocumentContent.value);
    rejectionDocumentContent.value = null

    Events.dispatch(Events.PRIME_CONTEXT, [
      {
        kind: 'user',
        text: 'I rejected your draft.'
      },
      {
        kind: 'ai',
        text: 'OK.'
      }
    ])
    capturePosthog('ai_draft_rejected')
  }


  Events.listen(Events.UPDATE_DOCUMENT, e => {

    const dmp = new diff_match_patch();

    function getDiffRanges(oldText, newText) {
      const diffs = dmp.diff_main(oldText, newText);
      dmp.diff_cleanupSemantic(diffs);

      let index = 0; 
      const added = [];
      const removed = [];

      for (const [op, data] of diffs) {
        if (op === 0) {
          // equal: advance position
          index += data.length;
        } else if (op === 1) {
          // insertion: mark highlight range in NEW text
          added.push([index, index + data.length + 1]);
          index += data.length;
        } else if (op === -1) {
          // deletion: doesn’t move position in NEW text
          removed.push([index, index]); 
        }
      }
      return { added, removed };
    }

    function updateWithDiff(editor, newHtmlText) {
      const oldContent = editor.getJSON()
      const oldText = editor.getText(); 
      
      editor.commands.setContent(newHtmlText);

      setTimeout(() => {
        const newText = editor.getText(); 
        const { added, removed } = getDiffRanges(oldText, newText);
        
        editor.chain().focus()
          .dropHighlights()
          .highlightRanges(added)
        .run();      
        rejectionDocumentContent.value = oldContent

      }, 10)
    }

    updateWithDiff(editor.value, e.detail.content)
  })

  const listenerGroup = Events.group();



</script>

<template>
  <Toolbar :editor="editor" />
  <AiPopup :editor="editor" v-if="state.aiPopup !== null" @close="state.aiPopup = null" :key="state.aiPopup" />

  <div class="TheEditor" ref="editorNode" @click="makeSureFocus" @drop.prevent="handleDrop">
    <article class="prose prose-md md:prose-sm">
      <EditorContent :editor="editor" />
      <Gutter :editor="editor"/>
    </article>
    <DraftOverlay v-if="draftText !== null && !draftGenerated" />    
  </div>
  <Button v-if="rejectionDocumentContent !== null"
      title="revert the latest AI changes" 
      @action="rejectNewVersionDraft"
      style="position: absolute; right: 30px; bottom: 30px;"
      class="btn btn-lg btn-primary btn-circle"><Icon :size="25" name="tdesign--rollback"/></Button>

</template>

<style lang="less">

.TheEditor {
  --editor-content-paddings: 5px 38px;

  @media (max-width: (@light-mode-bp - 1px)) { 
    --editor-content-paddings: 5px 10px;    
  }


  align-self: stretch;
  flex: 1 1 0; 
  overflow: auto;
  position: relative;
  // font-size: 13px;
}
.ProseMirror {
  padding: var(--editor-content-paddings);  
}
.TheEditor .ProseMirror p {
  // width: 100%;
  // height: 100%;
  
  // font-size: 1.125em;
  line-height: 1.7 !important;
}
.ProseMirror:focus {
  outline: none;
}

.ProseMirror li p {
  margin: 0;
}

.tiptap ul[data-type=taskList] {
  /* list-style:none; */
  /* margin-left:0; */
  padding: 0
}

.tiptap ul[data-type=taskList] li {
  align-items: flex-start;
  display: flex
}

.tiptap ul[data-type=taskList] li>label {
  flex: 0 0 auto;
  margin-right: .5rem;
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none
}

.tiptap ul[data-type=taskList] li>div {
  flex: 1 1 auto
}

.tiptap ul[data-type=taskList] input[type=checkbox] {
  cursor: pointer;
  transform: scale(1.2);

}

.tiptap ul[data-type=taskList] ul[data-type=taskList] {
  margin: 0;

}

.tiptap .collaboration-cursor__caret {
    border-left: 1px solid #0d0d0d;
    border-right: 1px solid #0d0d0d;
    margin-left: -1px;
    margin-right: -1px;
    pointer-events: none;
    position: relative;
    word-break: normal
}

.tiptap .collaboration-cursor__label {
    border-radius: 3px 3px 3px 0;
    color: #0d0d0d;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    left: -1px;
    line-height: normal;
    padding: .1rem .3rem;
    position: absolute;
    top: -1.4em;
    -webkit-user-select: none;
    -moz-user-select: none;
    user-select: none;
    white-space: nowrap
}

</style>